/**
 * Ficha do usuario — /configuracoes/acessos/usuarios/:id.
 *
 * Layout em 3 zonas (P1):
 *
 *   ZONA 1 (EntityHeader): avatar, nome, email, badges, menu de acoes
 *   ZONA 2 (ContextRail):  KPIs do usuario (efetivas, conflitos, etc)
 *   ZONA 3 (Blocks):
 *     - Cargo
 *     - Permissoes efetivas (read-only, agrupado)
 *     - Overrides individuais (interativo com PermissionMatrix)
 *     - MFA
 *     - Auditoria recente
 *
 * SoD detectado em tempo real durante edicao de overrides (P2). Save
 * com debounce de 400ms + toast de undo seria ideal (P12); aqui usamos
 * "Salvar overrides" explicito + revert ao alterar.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiCopy,
  FiKey,
  FiMail,
  FiShield,
  FiUnlock,
  FiUserCheck,
  FiUsers,
  FiUserX,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import AuditTimeline from "../../../components/AuditTimeline";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import MfaEnrollmentPanel from "../../../components/MfaEnrollmentPanel";
import PermissionMatrix from "../../../components/PermissionMatrix";
import { criarNomePermissao } from "../../../components/PermissionMatrix/arvore.js";
import SlideOverPanel from "../../../components/SlideOverPanel";
import SodWarningBanner from "../../../components/SodWarningBanner";
import UserAvatar from "../../../components/UserAvatar";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";

import {
  enviarConvite,
  fetchAuditoria,
  fetchCargoEfetivas,
  fetchCargos,
  fetchCatalogoPermissoes,
  fetchUsuario,
  fetchUsuarioEfetivas,
  patchUsuario,
  patchUsuarioAtivar,
  patchUsuarioMfaForcarReset,
  putUsuarioOverrides,
  resetarSenha,
} from "../api";
import {
  ActionsBar,
  Blocks,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ContextRail,
  EmptyMsg,
  ErrorMsg,
  Field,
  InfoMsg,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
  ThreeZoneLayout,
} from "../styles.jsx";

import { mensagemDeStatusDoUsuario } from "./statusUsuario";

function badgesUsuario(usuario, totais) {
  if (!usuario) return [];
  const badges = [];
  if (!usuario.ativo) badges.push({ label: "Inativo", tone: "danger" });
  else if (usuario.primeiro_acesso) badges.push({ label: "1º acesso pendente", tone: "warning" });
  else badges.push({ label: "Ativo", tone: "success" });

  if (usuario.mfa_obrigatorio) badges.push({ label: "MFA exigido", tone: "info" });
  if (totais?.conflitos_sod > 0) {
    badges.push({
      label: `${totais.conflitos_sod} conflito(s) SoD`,
      tone: "danger",
      icon: <FiAlertTriangle size={12} />,
    });
  }
  return badges;
}

function UsuarioDetalhe() {
  const { usuarioId } = useParams();
  const navigate = useNavigate();
  const { request, usuario: usuarioLogado, hasPermission } = useAuth();
  // Cada botão confere a permissão do endpoint que ele chama.
  const podeEditar = hasPermission("usuario:edit");
  const podeResetarSenha = hasPermission("usuario:reset_password");
  const podeDesativar = hasPermission("usuario:disable");
  const podeResetarMfa = hasPermission("mfa:disable_others");
  const podeVerAuditoria = hasPermission("auditoria:view");
  const podeVerCargo = hasPermission("cargo:view");
  const toast = useToast();

  const [usuario, setUsuario] = useState(null);
  const [efetivas, setEfetivas] = useState(null);
  const [catalogo, setCatalogo] = useState(null);
  const nomePermissao = useMemo(
    () => criarNomePermissao(catalogo?.modulos || []),
    [catalogo]
  );
  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Self-service do MFA: o usuario que esta vendo a ficha e o mesmo do
  // recurso. Apenas nesse caso o painel de enrollment aparece — admins
  // veem o status mas nao configuram MFA alheio (so podem forcar reset).
  const ehProprioUsuario = String(usuarioLogado?.id) === String(usuarioId);
  const [mfaPanelOpen, setMfaPanelOpen] = useState(false);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridesSelecionados, setOverridesSelecionados] = useState(new Set());
  // Bloco Parenteses 4: revokes individuais. Permitem negar permissoes
  // herdadas sem precisar mexer no cargo pai (que afetaria outros filhos).
  const [overridesRevogados, setOverridesRevogados] = useState(new Set());
  const [salvandoOverrides, setSalvandoOverrides] = useState(false);
  const [sodPendente, setSodPendente] = useState(null); // {conflitos, requer_aceite}
  const [msgSucesso, setMsgSucesso] = useState(null);
  // Reset de senha: a senha provisoria gerada e exibida UMA vez ao Master num
  // painel copiavel. A API nao reexibe (no banco fica so o hash irreversivel).
  const [senhaResetada, setSenhaResetada] = useState(null);

  // Troca de cargo (Sprint pos-CRUD): SlideOverPanel com Select +
  // preview do diff de permissoes herdadas antes de confirmar.
  const [trocarCargoOpen, setTrocarCargoOpen] = useState(false);
  const [cargosDisponiveis, setCargosDisponiveis] = useState([]);
  const [cargoSelecionadoId, setCargoSelecionadoId] = useState("");
  const [previewEfetivasNovo, setPreviewEfetivasNovo] = useState(null);
  const [carregandoPreview, setCarregandoPreview] = useState(false);
  const [salvandoCargo, setSalvandoCargo] = useState(false);

  // Carregamento resiliente (Sprint 6 / debito tecnico):
  //  - ESSENCIAL: dados do usuario + efetivas + catalogo. Falha aqui
  //    impede renderizar a ficha (tem que mostrar erro).
  //  - OPCIONAL: auditoria. Se falhar, a ficha renderiza sem o bloco de
  //    historico e mostra warning discreto.
  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarnings([]);
    try {
      // Carrega essenciais com Promise.all (rejeita se algum falhar).
      const [u, e, c] = await Promise.all([
        fetchUsuario(request, usuarioId),
        fetchUsuarioEfetivas(request, usuarioId),
        fetchCatalogoPermissoes(request, true),
      ]);
      setUsuario(u?.data || u);
      setEfetivas(e);
      setCatalogo(c);
      // Inicializa selecao do PermissionMatrix com overrides grant atuais.
      const grants = new Set(
        (e?.overrides || [])
          .filter((o) => o.efeito === "grant")
          .map((o) => o.codigo)
      );
      setOverridesSelecionados(grants);
      // Bloco Parenteses 4: tambem carrega os revokes (overrides do tipo
      // "negar permissao herdada") para popular o estado `revoked` no chip.
      const revokes = new Set(
        (e?.overrides || [])
          .filter((o) => o.efeito === "revoke")
          .map((o) => o.codigo)
      );
      setOverridesRevogados(revokes);

      // Opcional: auditoria (degrada graciosamente; so com auditoria:view).
      try {
        const a = !podeVerAuditoria ? { resultados: [] } : await fetchAuditoria(request, {
          alvo_tipo: "usuario",
          alvo_id: usuarioId,
          por_pagina: 10,
        });
        setAuditoria(a?.resultados || []);
      } catch (errOpt) {
        setAuditoria([]);
        setWarnings((w) => [
          ...w,
          { key: "auditoria", message: "Histórico de auditoria indisponível no momento." },
        ]);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar usuário.");
    } finally {
      setLoading(false);
    }
  }, [request, usuarioId, podeVerAuditoria]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const herdadasSet = useMemo(
    () => new Set(efetivas?.herdadas || []),
    [efetivas]
  );
  const efetivasSet = useMemo(
    () => new Set(efetivas?.efetivas || []),
    [efetivas]
  );

  const totalEfetivas = efetivas?.totais?.efetivas || 0;
  const totalHerdadas = efetivas?.totais?.herdadas || 0;
  const totalOverrides = efetivas?.totais?.overrides || 0;
  const totalConflitos = efetivas?.totais?.conflitos_sod || 0;

  // Bloco Parenteses 4: handler suporta 4 estados — grant, revoke,
  // default (limpa override), e backward-compat com booleans (true=grant,
  // false=clear).
  const handleToggleOverride = (codigo, novoEstado) => {
    // Backward-compat: boolean true => "granted", false => "default"
    let estado;
    if (novoEstado === true) estado = "granted";
    else if (novoEstado === false) estado = "default";
    else estado = String(novoEstado || "default");

    if (estado === "granted" || estado === "checked") {
      setOverridesSelecionados((prev) => {
        const novo = new Set(prev);
        novo.add(codigo);
        return novo;
      });
      // Granting tambem limpa revoke se houver (mutuamente exclusivos).
      setOverridesRevogados((prev) => {
        if (!prev.has(codigo)) return prev;
        const novo = new Set(prev);
        novo.delete(codigo);
        return novo;
      });
    } else if (estado === "revoked") {
      setOverridesRevogados((prev) => {
        const novo = new Set(prev);
        novo.add(codigo);
        return novo;
      });
      // Revogando tambem limpa grant se houver (improvavel mas defensivo).
      setOverridesSelecionados((prev) => {
        if (!prev.has(codigo)) return prev;
        const novo = new Set(prev);
        novo.delete(codigo);
        return novo;
      });
    } else {
      // "default", "off", "inherited" → limpa ambos
      setOverridesSelecionados((prev) => {
        if (!prev.has(codigo)) return prev;
        const novo = new Set(prev);
        novo.delete(codigo);
        return novo;
      });
      setOverridesRevogados((prev) => {
        if (!prev.has(codigo)) return prev;
        const novo = new Set(prev);
        novo.delete(codigo);
        return novo;
      });
    }
  };

  const construirPayloadOverrides = () => {
    const grants = Array.from(overridesSelecionados).map((codigo) => ({
      codigo,
      efeito: "grant",
      motivo: "Override individual configurado via /configuracoes/acessos/usuarios",
    }));
    const revokes = Array.from(overridesRevogados).map((codigo) => ({
      codigo,
      efeito: "revoke",
      motivo: "Permissao herdada revogada individualmente via /configuracoes/acessos/usuarios",
    }));
    return [...grants, ...revokes];
  };

  const salvarOverrides = async (opts = {}) => {
    setSalvandoOverrides(true);
    setError(null);
    setSodPendente(null);
    setMsgSucesso(null);
    try {
      const payload = await putUsuarioOverrides(
        request,
        usuarioId,
        construirPayloadOverrides(),
        // silentError porque tratamos abaixo (SoD pendente vira fluxo
        // proprio; erros genericos viram toast.error explicito).
        { ...opts, silentError: true },
      );
      const qtd = payload?.overrides_atualizados ?? 0;
      setMsgSucesso(`Overrides atualizados: ${qtd}.`);
      toast.success(qtd === 1 ? "1 override salvo." : `${qtd} overrides salvos.`);
      await carregar();
      setOverrideOpen(false);
    } catch (err) {
      // 409 com payload de SoD pendente: extras vem em err.payload se disponivel.
      const conflitos =
        err?.payload?.conflitos || err?.body?.conflitos || null;
      const requer_aceite =
        err?.payload?.requer_aceite || err?.body?.requer_aceite || false;
      if (conflitos) {
        setSodPendente({ conflitos, requer_aceite });
        toast.warning(
          "Conflito de SoD detectado. Revise antes de aceitar.",
          { dedupKey: "override-sod-pendente" },
        );
      } else {
        const msg = err?.message || "Erro ao salvar overrides.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSalvandoOverrides(false);
    }
  };

  const onAceitarSod = (justificativa) => {
    salvarOverrides({ aceiteConflito: true, justificativa });
  };

  const handleAtivar = async (ativo) => {
    try {
      // Desativar zera o `responsavel_padrao` dos formularios deste vendedor:
      // a resposta diz quantos, para a captacao do site nao trocar de dono em
      // silencio (FORM-21).
      const resposta = await patchUsuarioAtivar(request, usuarioId, ativo);
      setMsgSucesso(
        mensagemDeStatusDoUsuario(ativo, resposta?.formularios_responsavel_removido)
      );
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao alterar status do usuário.");
    }
  };

  const handleResetarSenha = async () => {
    setMsgSucesso(null);
    setError(null);
    try {
      const payload = await resetarSenha(request, usuarioId);
      // A API de reset devolve a senha provisoria no campo `nova_senha`
      // (a de criacao usa `senha_provisoria`); aceitamos ambos por robustez.
      const senha =
        payload?.data?.nova_senha ||
        payload?.nova_senha ||
        payload?.data?.senha_provisoria ||
        payload?.senha_provisoria ||
        null;
      if (senha) {
        setSenhaResetada(senha);
      } else {
        setMsgSucesso(
          "Senha resetada, mas a senha provisoria não veio na resposta. Resete novamente."
        );
      }
      // Atualiza a ficha (badge "1o acesso pendente"). Falha aqui nao e
      // critica — a senha ja foi exibida no painel.
      try {
        await carregar();
      } catch {
        /* nao critico */
      }
    } catch (err) {
      setError(err?.message || "Erro ao resetar senha.");
    }
  };

  const copiarSenhaResetada = async () => {
    if (!senhaResetada) return;
    try {
      await navigator.clipboard.writeText(senhaResetada);
      toast.success("Senha copiada.");
    } catch {
      toast.error("Não deu pra copiar. Selecione o texto e copie manualmente.");
    }
  };

  const handleForcarResetMfa = async () => {
    if (
      !window.confirm(
        `Desativar o MFA de ${usuario?.nome}? O usuário perderá o segundo fator e precisará reconfigurar.`
      )
    ) {
      return;
    }
    try {
      await patchUsuarioMfaForcarReset(request, usuarioId);
      setMsgSucesso("MFA do usuário desativado. Solicite que ele reconfigure no próximo login.");
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao desativar MFA.");
    }
  };

  const handleEnviarConvite = async () => {
    try {
      const payload = await enviarConvite(request, usuarioId);
      const enviado = payload?.data?.email_enviado || payload?.email_enviado;
      setMsgSucesso(
        enviado
          ? "Convite enviado por email. Expira em 24h."
          : "Convite gerado, mas o envio de email falhou (verifique configuração SMTP)."
      );
    } catch (err) {
      setError(err?.message || "Erro ao enviar convite.");
    }
  };

  const abrirTrocarCargo = async () => {
    setError(null);
    setMsgSucesso(null);
    setCargoSelecionadoId(usuario?.cargo?.id || "");
    setPreviewEfetivasNovo(null);
    setTrocarCargoOpen(true);
    // Lazy load da lista de cargos.
    if (cargosDisponiveis.length === 0) {
      try {
        const payload = await fetchCargos(request, true);
        setCargosDisponiveis(payload?.resultados || []);
      } catch {
        setCargosDisponiveis([]);
      }
    }
  };

  // Quando o cargo selecionado muda no painel, busca as permissoes
  // herdadas do novo cargo para calcular o diff antes do admin confirmar.
  useEffect(() => {
    if (!trocarCargoOpen || !cargoSelecionadoId) {
      setPreviewEfetivasNovo(null);
      return;
    }
    if (cargoSelecionadoId === (usuario?.cargo?.id || "")) {
      setPreviewEfetivasNovo(null);
      return;
    }
    let active = true;
    setCarregandoPreview(true);
    (async () => {
      try {
        const payload = await fetchCargoEfetivas(request, cargoSelecionadoId);
        if (active) setPreviewEfetivasNovo(payload);
      } catch {
        if (active) setPreviewEfetivasNovo({ efetivas: [], erro: true });
      } finally {
        if (active) setCarregandoPreview(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [trocarCargoOpen, cargoSelecionadoId, request, usuario]);

  const diffCargo = useMemo(() => {
    if (!previewEfetivasNovo) return null;
    const herdadasAtuais = new Set(efetivas?.herdadas || []);
    const efetivasNovo = new Set(previewEfetivasNovo?.efetivas || []);
    const ganhos = [...efetivasNovo].filter((c) => !herdadasAtuais.has(c)).sort();
    const perdas = [...herdadasAtuais].filter((c) => !efetivasNovo.has(c)).sort();
    return { ganhos, perdas };
  }, [previewEfetivasNovo, efetivas]);

  const handleTrocarCargo = async () => {
    setError(null);
    setMsgSucesso(null);
    if (!cargoSelecionadoId) {
      setError("Selecione um cargo.");
      return;
    }
    if (cargoSelecionadoId === (usuario?.cargo?.id || "")) {
      setError("O usuário já está neste cargo.");
      return;
    }
    setSalvandoCargo(true);
    try {
      await patchUsuario(request, usuarioId, { cargo: cargoSelecionadoId });
      setMsgSucesso("Cargo do usuário atualizado.");
      setTrocarCargoOpen(false);
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao trocar cargo.");
    } finally {
      setSalvandoCargo(false);
    }
  };

  if (loading) return <Card><EmptyMsg>Carregando ficha do usuário...</EmptyMsg></Card>;
  if (error && !usuario) return <Card><ErrorMsg>{error}</ErrorMsg></Card>;
  if (!usuario) return <Card><EmptyMsg>Usuário não encontrado.</EmptyMsg></Card>;

  const totais = efetivas?.totais || {};
  const cargoNome = usuario.cargo?.nome || usuario.cargo_nome || "—";

  const kpis = [
    {
      key: "efetivas",
      label: "Permissões efetivas",
      value: totalEfetivas,
      tone: "default",
    },
    {
      key: "herdadas",
      label: "Herdadas do cargo",
      value: totalHerdadas,
      tone: "info",
    },
    {
      key: "overrides",
      label: "Ajustes individuais",
      value: totalOverrides,
      tone: totalOverrides > 0 ? "warning" : "default",
    },
    {
      key: "sod",
      label: "Conflitos SoD",
      value: totalConflitos,
      tone: totalConflitos > 0 ? "danger" : "default",
    },
  ];

  const proximaAcao =
    totalConflitos > 0 && podeEditar
      ? {
          label: "Atenção",
          title: "Resolver conflito SoD",
          tone: "danger",
          cta: "Ajustar permissões",
          onClick: () => setOverrideOpen(true),
        }
      : null;

  const acoesMenu = [
    podeEditar && {
      label: "Trocar cargo",
      icon: <FiUsers size={14} />,
      onClick: abrirTrocarCargo,
    },
    podeResetarSenha && {
      label: "Resetar senha",
      icon: <FiKey size={14} />,
      onClick: handleResetarSenha,
    },
    podeEditar && {
      label: "Enviar convite por email",
      icon: <FiMail size={14} />,
      onClick: handleEnviarConvite,
    },
    podeResetarMfa && {
      label: "Forçar reset do MFA",
      icon: <FiUnlock size={14} />,
      onClick: handleForcarResetMfa,
      danger: true,
    },
    podeDesativar &&
      (usuario.ativo
        ? {
            label: "Desativar usuário",
            icon: <FiUserX size={14} />,
            onClick: () => handleAtivar(false),
            danger: true,
          }
        : {
            label: "Reativar usuário",
            icon: <FiUserCheck size={14} />,
            onClick: () => handleAtivar(true),
          }),
  ].filter(Boolean);

  return (
    <>
      <EntityHeader
        onBack={() => navigate("/configuracoes/acessos/usuarios")}
        backLabel="Usuários"
        leading={
          <UserAvatar
            src={usuario.foto_url}
            nome={usuario.nome}
            email={usuario.email}
            size="lg"
          />
        }
        title={usuario.nome}
        subtitle={`${usuario.email} · ${cargoNome}`}
        badges={badgesUsuario(usuario, totais)}
        primaryAction={
          podeEditar
            ? {
                label: "Ajustar permissões",
                icon: <FiShield size={14} />,
                onClick: () => setOverrideOpen(true),
              }
            : undefined
        }
        actions={acoesMenu}
      />

      {msgSucesso && <InfoMsg style={{ marginBottom: 12 }}>{msgSucesso}</InfoMsg>}
      {error && <ErrorMsg style={{ marginBottom: 12 }}>{error}</ErrorMsg>}
      {warnings.length > 0 && (
        <InfoMsg style={{ marginBottom: 12 }}>
          {warnings.map((w) => (
            <div key={w.key}>⚠ {w.message}</div>
          ))}
        </InfoMsg>
      )}

      <ThreeZoneLayout>
        <ContextRail>
          <KpiStrip
            items={kpis}
            orientation="vertical"
            nextAction={proximaAcao}
          />
        </ContextRail>

        <Blocks>
          <Card>
            <CardHeader>
              <CardTitle>Cargo</CardTitle>
              {podeEditar && (
                <PrimaryButton type="button" onClick={abrirTrocarCargo}>
                  <FiUsers size={14} /> Trocar cargo
                </PrimaryButton>
              )}
            </CardHeader>
            <ActionsBar>
              <Pill $tone="neutral">{cargoNome}</Pill>
              <SecondaryButton
                type="button"
                onClick={() =>
                  usuario.cargo?.id && navigate(`/configuracoes/acessos/cargos/${usuario.cargo.id}`)
                }
                disabled={!usuario.cargo?.id || !podeVerCargo}
              >
                Ver ficha do cargo
              </SecondaryButton>
            </ActionsBar>
            <CardSubtitle>
              O cargo do usuário é a fonte principal das permissões. Para
              ajustes pontuais, use os ajustes individuais.
            </CardSubtitle>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                Permissões efetivas
                <small style={{ opacity: 0.6, marginLeft: 8, fontWeight: 400 }}>
                  {totalEfetivas} efetivas · {totalHerdadas} herdadas
                </small>
              </CardTitle>
            </CardHeader>
            {efetivas?.conflitos_sod?.length > 0 && (
              <SodWarningBanner conflitos={efetivas.conflitos_sod} nomePermissao={nomePermissao} />
            )}
            <PermissionMatrix
              catalogo={catalogo}
              selecionados={efetivasSet}
              herdados={herdadasSet}
              onToggle={() => {}}
              disabled
              vazioMensagem="Sem permissões efetivas."
            />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ajustes individuais</CardTitle>
              {podeEditar && (
                <PrimaryButton type="button" onClick={() => setOverrideOpen(true)}>
                  <FiShield size={14} /> Editar
                </PrimaryButton>
              )}
            </CardHeader>
            {efetivas?.overrides?.length ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {efetivas.overrides.map((o) => (
                  <li key={o.id || o.codigo} style={{ fontSize: 13 }}>
                    <strong>{o.efeito === "grant" ? "Liberada:" : "Negada:"}</strong>{" "}
                    <span title={o.codigo}>{nomePermissao(o.codigo)}</span>
                    {o.motivo ? ` — ${o.motivo}` : ""}
                    {o.expira_em ? ` (expira em ${o.expira_em.slice(0, 10)})` : ""}
                  </li>
                ))}
              </ul>
            ) : (
              <CardSubtitle>
                Nenhum ajuste individual. As permissões vêm todas do cargo.
              </CardSubtitle>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>MFA</CardTitle>
              {ehProprioUsuario && (
                <PrimaryButton
                  type="button"
                  onClick={() => setMfaPanelOpen(true)}
                >
                  Configurar MFA
                </PrimaryButton>
              )}
            </CardHeader>
            <ActionsBar>
              <Pill $tone={usuario.mfa_obrigatorio ? "info" : "neutral"}>
                {usuario.mfa_obrigatorio ? "Exigido pelo cargo" : "Não exigido"}
              </Pill>
              {usuario.ultimo_login_mfa_em ? (
                <Pill $tone="success">
                  Última verificação: {usuario.ultimo_login_mfa_em.slice(0, 10)}
                </Pill>
              ) : (
                <Pill $tone="warning">Nunca verificado</Pill>
              )}
            </ActionsBar>
            <CardSubtitle>
              {ehProprioUsuario
                ? "Habilite o autenticador (Google Authenticator, Authy ou similar) para um segundo fator de segurança."
                : "Apenas o próprio usuário pode configurar o MFA. Como admin, você pode forçar reset via menu de ações."}
            </CardSubtitle>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Auditoria recente</CardTitle>
              <SecondaryButton
                type="button"
                onClick={() =>
                  navigate(`/configuracoes/acessos/auditoria?alvo_tipo=usuario&alvo_id=${usuario.id}`)
                }
              >
                Ver tudo
              </SecondaryButton>
            </CardHeader>
            <AuditTimeline
              events={auditoria}
              onEventClick={(id) => navigate(`/configuracoes/acessos/auditoria?evento=${id}`)}
              emptyMessage="Sem atividade nos últimos eventos."
            />
          </Card>
        </Blocks>
      </ThreeZoneLayout>

      {ehProprioUsuario && (
        <MfaEnrollmentPanel
          open={mfaPanelOpen}
          onClose={() => setMfaPanelOpen(false)}
          onCompleted={carregar}
        />
      )}

      {/* Trocar cargo do usuario — Select + preview de diff de permissoes */}
      <SlideOverPanel
        open={trocarCargoOpen}
        onClose={() => setTrocarCargoOpen(false)}
        title={`Trocar cargo de ${usuario.nome}`}
        subtitle={`Atualmente: ${cargoNome}. Selecione o novo cargo para ver o impacto antes de confirmar.`}
        width="min(95vw, 560px)"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => setTrocarCargoOpen(false)}
              disabled={salvandoCargo}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handleTrocarCargo}
              disabled={
                salvandoCargo ||
                !cargoSelecionadoId ||
                cargoSelecionadoId === (usuario?.cargo?.id || "")
              }
            >
              <FiCheckCircle size={14} /> Confirmar troca
            </PrimaryButton>
          </>
        }
      >
        <Field>
          Novo cargo
          <Select
            value={cargoSelecionadoId}
            onChange={(e) => setCargoSelecionadoId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {cargosDisponiveis.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
                {c.total_usuarios != null ? ` (${c.total_usuarios} usuário(s))` : ""}
                {c.exige_mfa ? " · MFA exigido" : ""}
              </option>
            ))}
          </Select>
        </Field>

        {cargoSelecionadoId && cargoSelecionadoId === (usuario?.cargo?.id || "") && (
          <InfoMsg>O usuário já está neste cargo.</InfoMsg>
        )}

        {carregandoPreview && (
          <CardSubtitle>Calculando impacto das permissões...</CardSubtitle>
        )}

        {diffCargo && (diffCargo.ganhos.length > 0 || diffCargo.perdas.length > 0) && (
          <Card style={{ background: "transparent", border: "1px dashed" }}>
            <CardTitle>Impacto nas permissões herdadas</CardTitle>
            {diffCargo.ganhos.length > 0 && (
              <>
                <CardSubtitle>
                  <strong style={{ color: "var(--success-text)" }}>
                    + {diffCargo.ganhos.length} permissão(ões) ganhas
                  </strong>{" "}
                  (herdadas do novo cargo):
                </CardSubtitle>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12,
                    maxHeight: 140,
                    overflow: "auto",
                  }}
                >
                  {diffCargo.ganhos.map((c) => (
                    <li key={`ganho-${c}`}>
                      <code>{c}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {diffCargo.perdas.length > 0 && (
              <>
                <CardSubtitle style={{ marginTop: 8 }}>
                  <strong style={{ color: "var(--danger-text)" }}>
                    − {diffCargo.perdas.length} permissão(ões) perdidas
                  </strong>{" "}
                  (não mais herdadas):
                </CardSubtitle>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12,
                    maxHeight: 140,
                    overflow: "auto",
                  }}
                >
                  {diffCargo.perdas.map((c) => (
                    <li key={`perda-${c}`}>
                      <code>{c}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <CardSubtitle style={{ marginTop: 8, opacity: 0.75 }}>
              Os ajustes individuais (liberadas e negadas só para esta pessoa) continuam — você
              pode revisá-los no bloco "Ajustes individuais".
            </CardSubtitle>
          </Card>
        )}

        {diffCargo &&
          diffCargo.ganhos.length === 0 &&
          diffCargo.perdas.length === 0 &&
          cargoSelecionadoId !== (usuario?.cargo?.id || "") && (
            <InfoMsg>
              O novo cargo tem exatamente as mesmas permissões que o atual.
            </InfoMsg>
          )}
      </SlideOverPanel>

      <SlideOverPanel
        open={overrideOpen}
        onClose={() => {
          setOverrideOpen(false);
          setSodPendente(null);
        }}
        title="Ajustar permissões só desta pessoa"
        subtitle={`Libere permissões além das do cargo "${cargoNome}", ou negue uma que vem do cargo só para esta pessoa.`}
        width="min(95vw, 880px)"
        footer={
          <>
            <SecondaryButton
              type="button"
              onClick={() => {
                setOverrideOpen(false);
                setSodPendente(null);
              }}
            >
              Cancelar
            </SecondaryButton>
            <AsyncButton
              variant="primary"
              onClick={() => salvarOverrides()}
              disabled={salvandoOverrides}
              // silentSuccess: o salvarOverrides ja dispara toast.success
              // com a contagem real ("3 overrides salvos"). silentError
              // porque o catch trata SoD pendente (warning) vs erro generico
              // (error toast) com mensagens especificas.
              silentSuccess
              silentError
            >
              <FiCheckCircle size={14} /> Salvar ajustes
            </AsyncButton>
          </>
        }
      >
        {/* Erro dentro do panel — antes ficava so fora (linhas 484-485)
            e ficava por tras do panel aberto, parecendo que "nada acontece"
            quando salvar falhava (ex.: 403 por MFA expirado). */}
        {error && (
          <ErrorMsg style={{ marginBottom: 12 }}>{error}</ErrorMsg>
        )}
        {sodPendente?.conflitos && (
          <SodWarningBanner
            nomePermissao={nomePermissao}
            conflitos={sodPendente.conflitos}
            onAcceptAnyway={
              sodPendente.requer_aceite ? onAceitarSod : undefined
            }
            onDismiss={() => setSodPendente(null)}
          />
        )}
        <PermissionMatrix
          catalogo={catalogo}
          selecionados={overridesSelecionados}
          herdados={herdadasSet}
          revogados={overridesRevogados}
          allowRevoke
          onToggle={handleToggleOverride}
          disabled={salvandoOverrides}
          vazioMensagem="Catálogo de permissões vazio."
        />
      </SlideOverPanel>

      {/* Reset de senha — senha provisoria exibida UMA vez, copiavel. */}
      <SlideOverPanel
        open={Boolean(senhaResetada)}
        onClose={() => setSenhaResetada(null)}
        title="Senha provisória gerada"
        subtitle={usuario?.nome}
        width="min(94vw, 460px)"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <InfoMsg>
            Esta senha aparece <strong>uma única vez</strong>. Copie e entregue
            ao usuário agora — ela não fica guardada e não pode ser vista de
            novo. No próximo login ele será obrigado a trocar por uma senha
            própria.
          </InfoMsg>
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink-2)",
                marginBottom: 6,
              }}
            >
              Senha provisória
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 14px",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                background: "var(--surface-2)",
              }}
            >
              <code
                style={{
                  flex: 1,
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  wordBreak: "break-all",
                  color: "var(--ink)",
                  userSelect: "all",
                }}
              >
                {senhaResetada}
              </code>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <PrimaryButton type="button" onClick={copiarSenhaResetada}>
              <FiCopy size={15} aria-hidden="true" /> Copiar senha
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => setSenhaResetada(null)}>
              Fechar
            </SecondaryButton>
          </div>
        </div>
      </SlideOverPanel>
    </>
  );
}

export default UsuarioDetalhe;
